// lib/queryClient.ts
import { QueryClient } from "@tanstack/react-query";

/**
 * QUERY_CONFIG — Tiered cache presets
 *
 * Spread these into useQuery calls instead of repeating the same options.
 *
 * STATIC      → Restaurant settings, loyalty config (admin-only changes)
 * OPERATIONAL → Menu items, ingredients, tables — mutated regularly by staff
 * REALTIME    → Orders, notifications, KDS — must stay current
 * ON_DEMAND   → Reports, analytics, audit logs — explicit navigation only
 *
 * ⚠️  KEY PRINCIPLE:
 * `invalidateQueries` marks a query stale. It only triggers an immediate
 * background re-fetch when there is an active observer (component is mounted).
 * If the component is NOT mounted at that moment, the data will only be
 * refreshed on next mount IF `refetchOnMount` is truthy.
 * → Leaving `refetchOnMount: false` globally means invalidated queries
 *   never get fresh data when the user navigates back to the page. That's
 *   why a hard refresh was needed. Fix: use `true` (or `"always"`) globally
 *   and opt individual STATIC queries out explicitly.
 */
export const QUERY_CONFIG = {
  /**
   * STATIC — Data changed only via explicit admin saves.
   * Cache aggressively; invalidate manually on mutation success.
   * These queries opt OUT of the global refetchOnMount behaviour.
   */
  STATIC: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Safe: mutations always invalidate explicitly
    refetchOnReconnect: false,
  },

  /**
   * OPERATIONAL — Data mutated regularly during restaurant operation.
   * Show cached data immediately, then re-fetch if stale on mount.
   * This is critical for inventory, menu items, unit conversions, etc.
   */
  OPERATIONAL: {
    staleTime: 60 * 1000, // 1 minute — short enough to catch recent mutations
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refresh when staff returns to the tab
    refetchOnMount: true, // Always check for stale data on navigation
    refetchOnReconnect: true,
  },

  /**
   * REALTIME — Orders, notifications, KDS.
   * Must stay current. Polling acceptable until WebSocket/SignalR is added.
   */
  REALTIME: {
    staleTime: 15 * 1000, // 15 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 30 * 1000, // Poll every 30 s
  },

  /**
   * ON_DEMAND — Reports, analytics, audit logs.
   * Only fetched when the user explicitly navigates to that screen.
   * Never auto-refresh.
   */
  ON_DEMAND: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      // ─────────────────────────────────────────────────────────────────
      // DEFAULT CACHE BEHAVIOUR (applies to every query unless overridden)
      //
      // staleTime: 0 means cached data is treated as stale immediately.
      // With refetchOnMount: true, mounting a component with cached data
      // will trigger a background refetch so navigation after
      // invalidateQueries() results in up-to-date data without a hard refresh.
      // The first render may still show previously cached data until that
      // refetch completes.

      // Queries that need more aggressive caching should spread
      // QUERY_CONFIG.STATIC (or another preset) in their options.
      // ─────────────────────────────────────────────────────────────────
      staleTime: 30 * 1000, // Treat all data as immediately stale by default
      gcTime: 10 * 60 * 1000, // Keep unused data in memory for 10 minutes
      refetchOnWindowFocus: false, // Opt-in per query to avoid noisy tab-switch refetches
      refetchOnMount: true, // ✅ Re-fetch stale data when component mounts
      refetchOnReconnect: true, // Always refresh after reconnect
    },
    mutations: {
      retry: 1,
      onError: (error: unknown) => {
        console.error("Mutation error:", error);
      },
    },
  },
});
